import React, { useEffect, useMemo, useRef, useState } from "react";

// utils
import moment from "moment";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { fetchRatesForDates } from "../../../redux/features/sales/ratesSlice";
import { fetchCouponDetails } from "../../../redux/features/coupons/couponsSlice";

// Styling
import { makeStyles } from "@material-ui/core/styles";

// Components
import { KeyboardDatePicker } from "@material-ui/pickers";
import MaterialTable, { MTableToolbar } from "material-table";
import EditableTableCell from "../../Table/EditableTableCell";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Paper from "@material-ui/core/Paper";
import Alert from "@material-ui/lab/Alert";
import HotelComboBox from "../HotelComboBox";
import RoomComboBox from "../RoomComboBox";
import MuiAlert from "@material-ui/lab/Alert";
import NumberFormatCustom from "../NumberFormatCustom";
import InputAdornment from "@material-ui/core/InputAdornment";
import IntegerInput from "../IntegerInput";
import CouponComboBox from "../CouponComboBox";
import RatesChangePreviewDialog from "../../Sales/Orders/RatesChangePreviewDialog";
import RatesSummary, {
  NEEDS_PRICE_COLOR,
} from "../../Sales/Orders/RatesSummary";

// Icons
import tableIcons from "../../Table/TableIcons";
import GetAppIcon from "@material-ui/icons/GetApp";

const useStyles = makeStyles((theme) => ({
  maxWidth: { width: "100%" },
  alert: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
  resize: {
    fontSize: theme.typography.caption.fontSize,
  },
  needsPriceChip: {
    backgroundColor: NEEDS_PRICE_COLOR,
    color: "#000",
    fontWeight: 600,
  },
  couponNotice: {
    marginTop: theme.spacing(1),
  },
  sectionLabel: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.light,
    fontSize: "0.75rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
}));

// Outlined like the summary below it, so the table reads as its own box
// inside the edit card instead of blending into it.
const RatesTableContainer = (props) => (
  <Paper {...props} elevation={0} variant="outlined" />
);

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const RATE_TYPE_OPTIONS = [
  { value: "day", label: "Regular Day Rate" },
  { value: "week", label: "Custom Week Rate" },
  { value: "month", label: "Custom Month Rate" },
];

const getRateTypeLabel = (rateType) =>
  RATE_TYPE_OPTIONS.find((option) => option.value === rateType)?.label ||
  "Regular Day Rate";

// Need to spread array so it doesn't fail
const formatData = (data) => {
  return data.map((o) => ({
    ...o,
  }));
};

const sortRatesByDate = (rates = []) => {
  return [...rates].sort(
    (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()
  );
};

const validateConsecutiveRates = (rates = []) => {
  const sortedRates = sortRatesByDate(rates);

  for (let i = 1; i < sortedRates.length; i += 1) {
    const previousDate = moment(sortedRates[i - 1].date).startOf("day");
    const currentDate = moment(sortedRates[i].date).startOf("day");

    if (currentDate.diff(previousDate, "days") !== 1) {
      return {
        valid: false,
        content:
          "The dates have to be all consecutive and no missing dates in the middle are allowed.",
      };
    }
  }

  return {
    valid: true,
    content: "",
  };
};

const buildRatesForRange = ({
  startDate,
  endDate,
  existingRates = [],
  defaultTax = 17,
}) => {
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");

  if (!start.isValid() || !end.isValid() || !end.isAfter(start, "day")) {
    return [];
  }

  const existingRatesByDate = new Map(
    existingRates.map((rate) => [moment(rate.date).format("YYYY-MM-DD"), rate])
  );
  const nextRates = [];
  const cursor = moment(start);

  while (cursor.isBefore(end, "day")) {
    const dateKey = cursor.format("YYYY-MM-DD");
    const existingRate = existingRatesByDate.get(dateKey);

    nextRates.push(
      existingRate
        ? { ...existingRate }
        : {
            date: dateKey,
            price: "",
            tax: defaultTax,
            // No real price yet; flagged in the table and blocks the preview
            needsPrice: true,
          }
    );

    cursor.add(1, "day");
  }

  return nextRates;
};

// Rendered inside the table toolbar. Keeps its own draft values so typing in it
// never re-renders (or remounts) the table.
const BulkRateControls = ({ controlsRef }) => {
  const classes = useStyles();
  const [price, setPrice] = useState("");
  const [tax, setTax] = useState("");
  const { hasRates, disabled, onApply } = controlsRef.current;

  if (!hasRates) return null;

  const handleApply = () => {
    if (onApply(price, tax)) {
      setPrice("");
      setTax("");
    }
  };

  return (
    <Box px={3} pb={2}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={6} sm={4}>
          <TextField
            className={classes.maxWidth}
            label="Price for All Nights"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            InputProps={{
              inputComponent: NumberFormatCustom,
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={6} sm={4}>
          <TextField
            className={classes.maxWidth}
            label="Tax for All Nights"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            inputProps={{
              isAllowed: ({ formattedValue, floatValue }) =>
                formattedValue === "" || floatValue <= 100,
            }}
            InputProps={{
              inputComponent: NumberFormatCustom,
              startAdornment: (
                <InputAdornment position="start">%</InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            disabled={disabled}
            onClick={handleApply}
          >
            Apply to All Nights
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

// Explains how the selected coupon will apply, using the same rules as the
// API: rates are discounted when the coupon covers the hotel or the package,
// add-ons only when it covers the package, and the total is capped at the
// coupon's max discount.
const CouponNotice = ({ details, hotelId, hotelName, packageId }) => {
  const classes = useStyles();

  if (!details) return null;

  if (details.error) {
    return (
      <Alert severity="error" className={classes.couponNotice}>
        Could not load the details for this coupon.
      </Alert>
    );
  }

  const amount = Number(details.discount_amount) || 0;
  const maxDiscount = Number(details.max_discount) || 0;
  const hotelMatch = (details.hotels || []).some((h) => h.id === hotelId);
  const packageMatch = (details.packages || []).some((p) => p.id === packageId);
  const cautions = [
    !details.enabled && "disabled",
    details.end_date && moment(details.end_date).isBefore(moment(), "day")
      ? "expired"
      : null,
    Number(details.uses_left) <= 0 && "out of uses",
  ].filter(Boolean);
  const cautionText =
    cautions.length > 0
      ? ` This coupon is ${cautions.join(
          " and "
        )}, but it will still be applied.`
      : "";

  if (!hotelMatch && !packageMatch) {
    return (
      <Alert severity="warning" className={classes.couponNotice}>
        This coupon does not cover {hotelName || "this hotel"} or this package,
        so the discount will be $0.{cautionText}
      </Alert>
    );
  }

  if (maxDiscount <= 0) {
    return (
      <Alert severity="warning" className={classes.couponNotice}>
        This coupon&apos;s max discount is $0, so no discount will apply.
        {cautionText}
      </Alert>
    );
  }

  const appliesTo = packageMatch ? "rates and add-ons" : "rates only";
  const description =
    details.type === "percentage"
      ? `${amount}% off ${appliesTo}`
      : packageMatch
      ? `${currencyFormat.format(amount)} off, split between rates and add-ons`
      : `${currencyFormat.format(
          amount / 2
        )} off rates (half of ${currencyFormat.format(
          amount
        )}; the other half only applies to add-ons when the coupon covers the package)`;

  return (
    <Alert
      severity={cautions.length > 0 ? "warning" : "info"}
      className={classes.couponNotice}
    >
      {description}. Max discount {currencyFormat.format(maxDiscount)}.
      {cautionText}
    </Alert>
  );
};

const RatesTableEdit = ({
  orderInfo,
  currentAmounts,
  currentHotelName,
  currentRoomName,
  currentCouponName,
  packageId,
  onPreview,
  onSubmit,
}) => {
  const defaultPeople = {
    adults: 1,
    children: 0,
    seniors: 0,
    babies: 0,
  };

  //Styles
  const classes = useStyles();

  // Local State
  const [fetchDisabled, setFetchDisabled] = useState(false);
  const [check_in, setCheckIn] = useState(
    orderInfo.check_in ? moment(orderInfo.check_in) : moment()
  );
  const [check_out, setCheckOut] = useState(
    orderInfo.check_out ? moment(orderInfo.check_out) : moment().add(1, "days")
  );
  const [hotel, setHotel] = useState({ id: orderInfo.hotel_id, name: "" });
  const [room, setRoom] = useState({ id: orderInfo.room_id, name: "" });
  const [package_name, setPackageName] = useState(orderInfo.package_name);
  const [people, setPeople] = useState({
    ...defaultPeople,
    ...orderInfo.people,
  });
  const [ratesData, setRatesData] = useState(
    sortRatesByDate(formatData(orderInfo.rates || []))
  );
  const [rateType, setRateType] = useState("day");
  const [loadedRateType, setLoadedRateType] = useState("day");
  const [hasFetchedRates, setHasFetchedRates] = useState(false);
  const [requiresRatesRefresh, setRequiresRatesRefresh] = useState(false);
  const [hasNoRatesForSelection, setHasNoRatesForSelection] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    content: "",
  });
  const [coupon, setCoupon] = useState(
    orderInfo.coupon_id ? { id: orderInfo.coupon_id } : null
  );
  const [pendingOrderInfo, setPendingOrderInfo] = useState(null);
  const [previewAmounts, setPreviewAmounts] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Redux
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state) => state.rates);
  const { hotelsList } = useSelector((state) => state.hotels);
  const { roomsList } = useSelector((state) => state.rooms);
  const { couponsList } = useSelector((state) => state.coupons);
  const activeRatesError = hasFetchedRates ? error : null;
  const defaultTax =
    ratesData.length > 0 ? ratesData[0].tax ?? "17.00" : "17.00";
  const rateTypeChanged = rateType !== loadedRateType;
  const needsRatesRefresh = requiresRatesRefresh || rateTypeChanged;
  const unpricedNights = ratesData.filter((rate) => rate.needsPrice).length;

  // Coupon details (covered hotels/packages, max discount) for the notice
  // under the coupon field, cached per coupon id.
  const [couponDetails, setCouponDetails] = useState({});
  const selectedCouponId = coupon?.id ?? null;

  useEffect(() => {
    if (!selectedCouponId || couponDetails[selectedCouponId]) return;

    dispatch(fetchCouponDetails(selectedCouponId)).then((res) => {
      if (!isMountedRef.current) return;
      setCouponDetails((current) => ({
        ...current,
        [selectedCouponId]: res.error ? { error: true } : res.coupon,
      }));
    });
  }, [dispatch, selectedCouponId, couponDetails]);

  const syncRatesData = (nextRatesData) => {
    const sortedRatesData = sortRatesByDate(formatData(nextRatesData));
    setRatesData(sortedRatesData);

    if (sortedRatesData.length > 0) {
      setCheckIn(moment(sortedRatesData[0].date));
      setCheckOut(
        moment(sortedRatesData[sortedRatesData.length - 1].date).add(1, "days")
      );
    }
  };

  const syncRatesToDateRange = (nextCheckIn, nextCheckOut) => {
    const rebuiltRates = buildRatesForRange({
      startDate: nextCheckIn,
      endDate: nextCheckOut,
      existingRates: ratesData,
      defaultTax,
    });

    setCheckIn(moment(nextCheckIn));
    setCheckOut(moment(nextCheckOut));
    setRatesData(sortRatesByDate(formatData(rebuiltRates)));
  };

  const invalidateRates = (content) => {
    setHasFetchedRates(false);
    setRequiresRatesRefresh(true);
    setHasNoRatesForSelection(false);
    setAlert({
      open: true,
      content,
    });
  };

  const handleHotelChange = (nextHotel) => {
    const nextHotelId = nextHotel?.id ?? null;
    const currentHotelId = hotel?.id ?? null;

    setHotel(nextHotel);

    if (nextHotelId !== currentHotelId) {
      setRoom(null);
      invalidateRates("Hotel changed. Fetch new rates before submitting.");
    }
  };

  const handleRoomChange = (nextRoom) => {
    const nextRoomId = nextRoom?.id ?? null;
    const currentRoomId = room?.id ?? null;

    setRoom(nextRoom);

    if (nextRoomId !== currentRoomId) {
      invalidateRates("Room changed. Fetch new rates before submitting.");
    }
  };

  const onFetchRates = async () => {
    setHasFetchedRates(true);

    const res = await dispatch(
      fetchRatesForDates(
        moment(check_in).format("YYYY-MM-DD"),
        moment(check_out).format("YYYY-MM-DD"),
        hotel.id,
        room.id,
        rateType
      )
    );

    if (!res?.error && res?.response?.length > 0) {
      const fetchedRateGroup = res.response[0];
      const formattedRates = (fetchedRateGroup.rates || []).map((rate) => ({
        ...rate,
        price: Number(rate.price),
        tax: Number(fetchedRateGroup.tax),
      }));

      syncRatesData(formattedRates);
      setLoadedRateType(rateType);
      setRequiresRatesRefresh(false);
      setHasNoRatesForSelection(false);
      setAlert({
        open: false,
        content: "",
      });
    } else if (!res?.error) {
      setLoadedRateType(rateType);
      setRequiresRatesRefresh(false);
      setHasNoRatesForSelection(true);
      setAlert({
        open: false,
        content: "",
      });
    }
  };

  const applyBulkValues = (bulkPrice, bulkTax) => {
    const hasBulkPrice = String(bulkPrice).trim() !== "";
    const hasBulkTax = String(bulkTax).trim() !== "";

    if (!hasBulkPrice && !hasBulkTax) {
      setAlert({
        open: true,
        content: "Enter a price or a tax to apply to every night.",
      });
      return false;
    }

    if (hasBulkPrice && (isNaN(Number(bulkPrice)) || Number(bulkPrice) < 0)) {
      setAlert({
        open: true,
        content: "Please enter a valid price of 0 or more.",
      });
      return false;
    }

    if (
      hasBulkTax &&
      (isNaN(Number(bulkTax)) || Number(bulkTax) < 0 || Number(bulkTax) > 100)
    ) {
      setAlert({
        open: true,
        content: "Tax must be between 0 and 100.",
      });
      return false;
    }

    syncRatesData(
      ratesData.map((rate) => ({
        ...rate,
        ...(hasBulkPrice
          ? { price: Number(bulkPrice), needsPrice: false }
          : {}),
        ...(hasBulkTax ? { tax: Number(bulkTax) } : {}),
      }))
    );

    setAlert({
      open: false,
      content: "",
    });

    return true;
  };

  // Read through a ref so the toolbar can stay a stable component type: a new
  // one on every render would remount the toolbar and wipe the table search.
  const bulkControlsRef = useRef({});
  bulkControlsRef.current = {
    hasRates: ratesData.length > 0,
    disabled: isLoading,
    onApply: applyBulkValues,
  };

  const RatesToolbar = useMemo(
    () =>
      function RatesTableToolbar(toolbarProps) {
        return (
          <>
            <MTableToolbar {...toolbarProps} />
            <BulkRateControls controlsRef={bulkControlsRef} />
          </>
        );
      },
    []
  );

  const handlePeopleChange = (field) => (e) => {
    const value = Number(e.target.value);

    setPeople((currentPeople) => ({
      ...currentPeople,
      [field]: Number.isNaN(value) ? 0 : value,
    }));
  };

  const validatePeople = () => {
    const normalizedPeople = {
      seniors: Number(people.seniors) || 0,
      adults: Number(people.adults) || 0,
      children: Number(people.children) || 0,
      babies: Number(people.babies) || 0,
    };

    for (const [field, value] of Object.entries(normalizedPeople)) {
      if (!Number.isInteger(value) || value < 0 || value > 25) {
        return {
          valid: false,
          content: `${
            field.charAt(0).toUpperCase() + field.slice(1)
          } must be an integer between 0 and 25.`,
        };
      }
    }

    if (normalizedPeople.seniors === 0 && normalizedPeople.adults < 1) {
      return {
        valid: false,
        content: "Adults must be at least 1 when seniors is 0.",
      };
    }

    return {
      valid: true,
      content: "",
      people: normalizedPeople,
    };
  };

  const formSubmit = async () => {
    const peopleValidation = validatePeople();
    if (!peopleValidation.valid) {
      setAlert({
        open: true,
        content: peopleValidation.content,
      });
      return;
    }

    if (needsRatesRefresh) {
      setAlert({
        open: true,
        content:
          "Hotel, room, or rate type changed. Fetch new rates before submitting changes.",
      });
      return;
    }

    if (hasNoRatesForSelection) {
      setAlert({
        open: true,
        content:
          "No rates are available for the selected dates, hotel, and room. Fetch or enter valid rates before submitting changes.",
      });
      return;
    }

    if (unpricedNights > 0) {
      setAlert({
        open: true,
        content: `${unpricedNights} night${
          unpricedNights === 1 ? " needs" : "s need"
        } a price. Click the price to enter one, use Price for All Nights, or Fetch New Rates.`,
      });
      return;
    }

    const formattedRates = sortRatesByDate(ratesData).map((rate) => ({
      date: moment(rate.date).format("YYYY-MM-DD"),
      price: rate.price,
      tax: Number(rate.tax ?? defaultTax),
    }));

    const { coupon_id: currentCouponId, ...orderInfoFields } = orderInfo;
    const nextCouponId = coupon?.id ?? null;

    const updatedOrderInfo = {
      ...orderInfoFields,
      // Only sent when changed: the API recalculates the discount whenever
      // coupon_id is present, otherwise the order keeps its current discount.
      ...(nextCouponId !== (currentCouponId ?? null)
        ? { coupon_id: nextCouponId }
        : {}),
      package_name,
      room_id: room.id,
      hotel_id: hotel.id,
      check_in: moment(check_in).format("YYYY-MM-DD"),
      check_out: moment(check_out).format("YYYY-MM-DD"),
      people: peopleValidation.people,
      rates: formattedRates,
    };

    setAlert({
      open: false,
      content: "",
    });

    setSubmitError(false);
    setIsPreviewing(true);

    const previewRes = await onPreview(updatedOrderInfo);

    if (!isMountedRef.current) return;
    setIsPreviewing(false);

    if (previewRes.error) {
      setAlert({
        open: true,
        content:
          previewRes.errorMessage ||
          "There was an error previewing the new pricing. Please try again.",
      });
      return;
    }

    setPreviewAmounts(previewRes.amounts);
    setPendingOrderInfo(updatedOrderInfo);
  };

  const confirmSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(false);

    const succeeded = await onSubmit(pendingOrderInfo);

    // On success the parent leaves edit mode and unmounts this form.
    if (!isMountedRef.current) return;

    setIsSubmitting(false);
    if (succeeded === false) {
      setSubmitError(true);
    } else {
      setPendingOrderInfo(null);
    }
  };

  const getHotelName = (selectedHotel) => {
    if (selectedHotel?.name) return selectedHotel.name;
    if (selectedHotel?.id === orderInfo.hotel_id) return currentHotelName;
    return hotelsList.find((h) => h.id === selectedHotel?.id)?.name || "";
  };

  const getRoomName = (selectedRoom) => {
    if (selectedRoom?.name) return selectedRoom.name;
    if (
      selectedRoom?.id === orderInfo.room_id &&
      hotel?.id === orderInfo.hotel_id
    )
      return currentRoomName;
    return roomsList.find((r) => r.id === selectedRoom?.id)?.name || "";
  };

  const getCouponName = (selectedCoupon) => {
    if (!selectedCoupon) return "";
    if (selectedCoupon.id === orderInfo.coupon_id) return currentCouponName;
    return couponsList.find((c) => c.id === selectedCoupon.id)?.name || "";
  };

  const validateData = (newData) => {
    const { date, price, tax } = newData;
    const hasPriceValue =
      price !== undefined && price !== null && String(price).trim() !== "";
    const hasTaxValue =
      tax !== undefined && tax !== null && String(tax).trim() !== "";

    let validateObj = {
      valid: true,
      content: "",
    };

    if (!date || !moment(date, "YYYY-MM-DD", true).isValid()) {
      validateObj = {
        valid: false,
        content: "Please enter a valid date in YYYY-MM-DD format.",
      };
    } else if (!hasPriceValue || isNaN(Number(price))) {
      validateObj = {
        valid: false,
        content: "Please enter a valid number for price.",
      };
    } else if (Number(price) < 0) {
      validateObj = {
        valid: false,
        content: "Price cannot be negative.",
      };
    } else if (!hasTaxValue || isNaN(Number(tax))) {
      validateObj = {
        valid: false,
        content: "Please enter a valid number for tax.",
      };
    } else if (Number(tax) < 0 || Number(tax) > 100) {
      validateObj = {
        valid: false,
        content: "Tax must be between 0 and 100.",
      };
    }

    return validateObj;
  };

  return (
    <form className={classes.form}>
      <Grid container spacing={3}>
        {/* Inputs */}
        <Grid item xs={12} lg={5}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                className={classes.maxWidth}
                required
                name="package_name"
                label="Package Name"
                fullWidth
                value={package_name}
                onChange={(e) => setPackageName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.sectionLabel}>Guests</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                className={classes.maxWidth}
                label="Adults"
                value={people.adults}
                onChange={handlePeopleChange("adults")}
                InputProps={{
                  inputComponent: IntegerInput,
                }}
                inputProps={{
                  min: 0,
                  max: 25,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                className={classes.maxWidth}
                label="Children"
                value={people.children}
                onChange={handlePeopleChange("children")}
                InputProps={{
                  inputComponent: IntegerInput,
                }}
                inputProps={{
                  min: 0,
                  max: 25,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                className={classes.maxWidth}
                label="Seniors"
                value={people.seniors}
                onChange={handlePeopleChange("seniors")}
                InputProps={{
                  inputComponent: IntegerInput,
                }}
                inputProps={{
                  min: 0,
                  max: 25,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                className={classes.maxWidth}
                label="Babies"
                value={people.babies}
                onChange={handlePeopleChange("babies")}
                InputProps={{
                  inputComponent: IntegerInput,
                }}
                inputProps={{
                  min: 0,
                  max: 25,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.sectionLabel}>Stay</Typography>
            </Grid>
            <Grid item xs={12}>
              <HotelComboBox
                defaultValue={hotel}
                onChange={handleHotelChange}
                readOnly={false}
                required
              />
            </Grid>
            {hotel && (
              <Grid item xs={12}>
                <RoomComboBox
                  key={hotel?.id || "no-hotel"}
                  defaultValue={room}
                  hotel_id={hotel.id}
                  onChange={handleRoomChange}
                  readOnly={false}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <KeyboardDatePicker
                className={classes.maxWidth}
                label="Check In"
                format="MM/dd/yyyy"
                value={check_in}
                onChange={(date) => {
                  if (!date || !moment(date).isValid()) return;
                  syncRatesToDateRange(date, check_out);
                }}
                onError={(e, v) => {
                  setFetchDisabled(!!e);
                }}
                KeyboardButtonProps={{
                  "aria-label": "change check in date",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <KeyboardDatePicker
                className={classes.maxWidth}
                disablePast
                minDate={moment(check_in).add(1, "days")}
                label="Check Out"
                format="MM/dd/yyyy"
                value={check_out}
                onChange={(date) => {
                  if (!date || !moment(date).isValid()) return;
                  syncRatesToDateRange(check_in, date);
                }}
                onError={(e, v) => {
                  setFetchDisabled(!!e);
                }}
                KeyboardButtonProps={{
                  "aria-label": "change check out date",
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.sectionLabel}>Pricing</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl className={classes.maxWidth}>
                <InputLabel shrink id="rate-type-label">
                  Rate Type
                </InputLabel>
                <Select
                  labelId="rate-type-label"
                  value={rateType}
                  onChange={(e) => setRateType(e.target.value)}
                >
                  {RATE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Rate set pulled in by Fetch New Rates below.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<GetAppIcon />}
                disabled={fetchDisabled || isLoading || !hotel?.id || !room?.id}
                onClick={onFetchRates}
              >
                Fetch New Rates
              </Button>
            </Grid>
            <Grid item xs={12}>
              <CouponComboBox
                label="Coupon"
                helperText="Clear to remove the coupon from this order."
                defaultValue={coupon}
                onChange={setCoupon}
              />
              <CouponNotice
                details={
                  selectedCouponId ? couponDetails[selectedCouponId] : null
                }
                hotelId={hotel?.id}
                hotelName={getHotelName(hotel)}
                packageId={packageId}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Rates */}
        <Grid item xs={12} lg={7}>
          {needsRatesRefresh && (
            <Alert severity="warning">
              <Typography variant="body1">
                {requiresRatesRefresh
                  ? "Hotel or room changed. Fetch new rates before submitting."
                  : `Rate type changed to ${getRateTypeLabel(
                      rateType
                    )}. Fetch new rates before submitting.`}
              </Typography>
            </Alert>
          )}
          {activeRatesError && (
            <Alert severity="error">
              <Typography variant="body1">
                Error fetching rates. Please try again.
              </Typography>
            </Alert>
          )}
          {hasNoRatesForSelection && !isLoading && (
            <Alert severity="info">
              <Typography variant="body1">
                No rates available for the selected dates, hotel, and room
                combination.
              </Typography>
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Try changing the dates, hotel, or room to get available rates.
              </Typography>
            </Alert>
          )}
          {alert.open && (
            <Grid item xs={12}>
              <MuiAlert
                className={classes.alert}
                onClose={() =>
                  setAlert({
                    open: false,
                    content: "",
                  })
                }
                severity="error"
              >
                {alert.content}
              </MuiAlert>
            </Grid>
          )}
          <MaterialTable
            title={
              <div>
                <Typography variant="h6">Rates by Date</Typography>
                <Typography variant="caption" color="textSecondary">
                  Click a price or tax to edit it. Enter saves, Esc cancels.
                </Typography>
              </div>
            }
            isLoading={isLoading}
            icons={tableIcons}
            components={{
              Toolbar: RatesToolbar,
              Cell: EditableTableCell,
              Container: RatesTableContainer,
            }}
            columns={[
              {
                title: "Date",
                field: "date",
                editable: "onAdd",
                // Dates are only entered when adding a night
                disableCellEdit: true,
                render: (rowData) => (
                  <>{moment(rowData.date).tz("America/Chicago").format("ll")}</>
                ),
                initialEditValue: moment(ratesData[ratesData.length - 1]?.date)
                  .add(1, "days")
                  .format("YYYY-MM-DD"),
                editComponent: (props) => (
                  <TextField
                    value={props.value}
                    onChange={(e) => props.onChange(e.target.value)}
                    InputProps={{
                      classes: {
                        input: classes.resize,
                      },
                    }}
                  />
                ),
              },
              {
                title: "Price",
                field: "price",
                type: "currency",
                render: (rowData) =>
                  rowData.needsPrice ? (
                    <Chip
                      size="small"
                      label="Needs price"
                      className={classes.needsPriceChip}
                    />
                  ) : (
                    currencyFormat.format(Number(rowData.price) || 0)
                  ),
                cellStyle: {
                  textAlign: "right",
                },
                headerStyle: {
                  textAlign: "right",
                },
                initialEditValue: 100,
                editComponent: (props) => (
                  <TextField
                    value={props.value}
                    onChange={(e) => props.onChange(e.target.value)}
                    inputProps={{
                      thousandSeparator: true,
                    }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      classes: {
                        input: classes.resize,
                      },
                    }}
                  />
                ),
              },
              {
                title: "Tax",
                field: "tax",
                type: "numeric",
                cellStyle: {
                  textAlign: "right",
                },
                headerStyle: {
                  textAlign: "right",
                },
                render: (rowData) => <>{`${rowData.tax ?? defaultTax}%`}</>,
                initialEditValue: defaultTax,
                editComponent: (props) => (
                  <TextField
                    value={props.value}
                    onChange={(e) => props.onChange(e.target.value)}
                    inputProps={{
                      isAllowed: ({ formattedValue, floatValue }) =>
                        formattedValue === "" || floatValue <= 100,
                      style: {
                        textAlign: "right",
                      },
                    }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                      classes: {
                        input: classes.resize,
                      },
                    }}
                  />
                ),
              },
            ]}
            data={ratesData}
            options={{
              search: false,
              paging: false,
              maxBodyHeight: 480,
              actionsColumnIndex: -1,
            }}
            cellEditable={{
              onCellEditApproved: (newValue, oldValue, rowData, columnDef) =>
                new Promise((resolve, reject) => {
                  const index = rowData.tableData.id;
                  const { tableData, ...currentRow } = ratesData[index];
                  const field = columnDef.field;
                  const candidate = { ...currentRow, [field]: newValue };
                  // An unpriced night can still have its tax edited first
                  const validateObj = validateData(
                    field === "tax" && currentRow.needsPrice
                      ? { ...candidate, price: 0 }
                      : candidate
                  );

                  if (!validateObj.valid) {
                    setAlert({ open: true, content: validateObj.content });
                    reject();
                    return;
                  }

                  const dataUpdate = [...ratesData];
                  dataUpdate[index] = {
                    ...candidate,
                    [field]: Number(newValue),
                    ...(field === "price" ? { needsPrice: false } : {}),
                  };
                  syncRatesData(dataUpdate);
                  setAlert({ open: false, content: "" });
                  resolve();
                }),
            }}
            editable={{
              onRowAddCancelled: (rowData) =>
                setAlert({
                  open: false,
                  content: "",
                }),
              onRowAdd: (newData) =>
                new Promise((resolve, reject) => {
                  const normalizedNewData = {
                    ...newData,
                    tax: newData.tax ?? defaultTax,
                  };
                  const validateObj = validateData(normalizedNewData);
                  const nextRatesData = [...ratesData, normalizedNewData];
                  const consecutiveValidation =
                    validateConsecutiveRates(nextRatesData);

                  if (validateObj.valid && consecutiveValidation.valid) {
                    syncRatesData(nextRatesData);
                    setAlert({
                      open: false,
                      content: "",
                    });

                    resolve();
                  } else {
                    setAlert({
                      open: true,
                      content:
                        validateObj.content || consecutiveValidation.content,
                    });
                    reject();
                  }
                }),
              onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  const dataDelete = [...ratesData];
                  const index = oldData.tableData.id;
                  dataDelete.splice(index, 1);
                  const consecutiveValidation =
                    validateConsecutiveRates(dataDelete);

                  if (consecutiveValidation.valid) {
                    syncRatesData(dataDelete);
                    setAlert({
                      open: false,
                      content: "",
                    });
                    resolve();
                  } else {
                    setAlert({
                      open: true,
                      content: consecutiveValidation.content,
                    });
                    reject();
                  }
                }),
            }}
            localization={{
              body: {
                emptyDataSourceMessage:
                  "No rates available for the selected criteria. Please try changing the dates, hotel, or room to get available rates.",
              },
            }}
          />

          <RatesSummary
            ratesData={ratesData}
            roomQty={Number(orderInfo.room_qty) || 1}
            note="Add-ons, resort fee and coupon discount are included in the preview."
          />

          {ratesData.length > 0 && !isLoading && !activeRatesError && (
            <Box mt={2} mb={3}>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                size="large"
                disabled={
                  isLoading ||
                  !ratesData.length ||
                  activeRatesError ||
                  needsRatesRefresh ||
                  hasNoRatesForSelection ||
                  isPreviewing
                }
                startIcon={
                  isPreviewing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
                onClick={formSubmit}
              >
                {isPreviewing ? "Calculating…" : "Preview New Pricing"}
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
      {pendingOrderInfo && previewAmounts && (
        <RatesChangePreviewDialog
          open
          onClose={() => setPendingOrderInfo(null)}
          onConfirm={confirmSubmit}
          isSubmitting={isSubmitting}
          submitError={submitError}
          roomQty={orderInfo.room_qty}
          amounts={currentAmounts || {}}
          nextAmounts={previewAmounts}
          current={{
            check_in: orderInfo.check_in,
            check_out: orderInfo.check_out,
            rates: orderInfo.rates || [],
            hotelName: currentHotelName,
            roomName: currentRoomName,
            couponName: currentCouponName,
          }}
          next={{
            check_in: pendingOrderInfo.check_in,
            check_out: pendingOrderInfo.check_out,
            rates: pendingOrderInfo.rates,
            hotelName: getHotelName(hotel),
            roomName: getRoomName(room),
            couponName: getCouponName(coupon),
          }}
        />
      )}
    </form>
  );
};

export default RatesTableEdit;
